import { useState } from "react";

import { IBillableDevice } from "../../../@types/admin/AdminBillableDevice";
import { useAddBillableDevice } from "../../../hooks/mutations/admin/billableDevices";
import { useBillableDeviceTypes } from "../../../hooks/queries/admin/billableDevices";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";

interface IAddBillableDeviceModalProps {
  existingDevices: IBillableDevice[];
  onClose: () => void;
}

/**
 * Add-billable-device modal (EN-5129, migrated from Retool
 * moduleBillableDevices/AddBillableModal). Device type comes from the
 * DeviceTypes catalog endpoint; picking a type that is already billable
 * shows the duplicate warning and disables Add — same client-side check as
 * Retool's VerifyIfDeviceExists script.
 */
export function AddBillableDeviceModal({
  existingDevices,
  onClose,
}: IAddBillableDeviceModalProps) {
  const { showToast } = useToast();
  const { deviceTypes, isLoading: deviceTypesLoading } =
    useBillableDeviceTypes();
  const { addBillableDevice, isAddingBillableDevice } = useAddBillableDevice();

  const [deviceType, setDeviceType] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [fdaNumber, setFdaNumber] = useState("");

  const isDuplicate =
    deviceType !== "" &&
    existingDevices.some((device) => device.deviceName === deviceType);

  const handleAdd = async () => {
    try {
      await addBillableDevice({
        deviceName: deviceType,
        manufacturer: manufacturer || undefined,
        fdaNumber: fdaNumber || undefined,
      });
      showToast("Billable device added successfully", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error adding the device to the billable list"
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Add billable device</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel>Device type</FilterLabel>
          <AdminSelectField
            value={deviceType}
            onChange={(e) => setDeviceType(e.target.value)}
            disabled={deviceTypesLoading}
          >
            <option value="">
              {deviceTypesLoading ? "Loading..." : "Select an option"}
            </option>
            {deviceTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        {isDuplicate && (
          <ErrorMessage>That device is already listed as billable</ErrorMessage>
        )}

        <FilterGroup>
          <FilterLabel>Manufacturer</FilterLabel>
          <FilterInput
            type="text"
            placeholder="Enter value"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>FDA number</FilterLabel>
          <FilterInput
            type="text"
            placeholder="Enter value"
            value={fdaNumber}
            onChange={(e) => setFdaNumber(e.target.value)}
          />
        </FilterGroup>

        <ModalFormActions>
          <FilterButton variant="secondary" onClick={onClose}>
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={!deviceType || isDuplicate || isAddingBillableDevice}
            onClick={handleAdd}
          >
            {isAddingBillableDevice ? "Adding..." : "Add"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
